/**
 * atmospherePlacement — pure placement math for the ambient layer (M3).
 * Deterministic seeding (djmsqrvve's Math.sin trick) so a level's fireflies/
 * god-rays sit at stable spots across reloads — no flicker, SSR-style. Split
 * out so the scatter is unit-testable without Pixi.
 */
import type {
  EffectAnchor,
  FireflySpec,
  GodRaySpec,
  GlowBlobSpec,
  WorldEffectAnchor,
} from '../../../config/atmosphere/presets.config';
import { lerp } from '../../../shared/utils/MathUtils';

/** Seeded pseudo-random in [0,1) — stable for a given seed. */
export function seededRand(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/** Hash a level id to a numeric base seed. */
export function hashSeed(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 1_000_000;
  return h + 1;
}

/** Per-layer placement variation (Scene FX v3 M2). */
export interface SeedVariation {
  /** Shift this layer's seed by a fixed amount (stable, but a different arrangement). */
  seedOffset?: number;
  /** Re-randomize this layer each mount (uses the AtmosphereLayer mount counter) instead of being stable. */
  varyOnReload?: boolean;
}

/**
 * Derive a layer's build seed from the scene base seed + its variation knobs.
 * Pure + deterministic: `mountCount` is a counter (NOT wall-clock/random), so a
 * `varyOnReload` layer still places deterministically WITHIN a given mount but
 * differently across mounts. Default (no knobs) → `baseSeed` unchanged, so
 * existing scenes keep their stable placement. The prime stride keeps successive
 * mounts well-separated (seededRand is sin-based — adjacent seeds already diverge,
 * but the stride makes it robust).
 */
export function deriveLayerSeed(baseSeed: number, layer: SeedVariation, mountCount: number): number {
  return baseSeed + (layer.seedOffset ?? 0) + (layer.varyOnReload ? mountCount * 7919 : 0);
}

export interface Bounds { width: number; height: number; }

/** A world-space rectangle to scatter ambiance into (camera view or level box). */
export interface Region { x0: number; y0: number; x1: number; y1: number; }

/** A region from level bounds (world origin) — the fallback when no camera. */
export function regionFromBounds(b: Bounds): Region {
  return { x0: 0, y0: 0, x1: b.width, y1: b.height };
}

/** A region from the camera view — a viewport-and-a-half around the center,
 *  biased upward so motes float in the air above the player. */
export function regionFromCamera(view: { centerX: number; centerY: number; width: number; height: number }): Region {
  // Keep the scatter roughly within the viewport (not 1.5×) so most motes
  // stay on-screen, biased to the upper-middle air band.
  const w = view.width * 0.52;
  const h = view.height * 0.55;
  return {
    x0: view.centerX - w,
    y0: view.centerY - h,
    x1: view.centerX + w,
    y1: view.centerY + h * 0.5,
  };
}

export interface PlacedFirefly {
  x: number; y: number; size: number; color: number;
  periodMs: number; driftX: number; driftY: number;
  alphaMin: number; alphaMax: number; phase: number;
}
export interface PlacedGodRay {
  x: number; y: number; width: number; height: number;
  color: number; tiltDeg: number; periodMs: number;
  alphaMin: number; alphaMax: number;
}
export interface PlacedGlowBlob { x: number; y: number; size: number; color: number; alpha: number; }

function isWorldEffectAnchor(anchor: EffectAnchor): anchor is WorldEffectAnchor {
  return typeof anchor.x === 'number' && typeof anchor.y === 'number';
}

/**
 * Resolve an atmosphere anchor to world pixels. Normalized anchors map through
 * the initial camera scatter region; world anchors preserve authored geometry.
 */
export function resolveEffectAnchor(anchor: EffectAnchor, r: Region): { x: number; y: number } {
  if (isWorldEffectAnchor(anchor)) return { x: anchor.x, y: anchor.y };
  return {
    x: lerp(r.x0, r.x1, anchor.atX),
    y: lerp(r.y0, r.y1, anchor.atY),
  };
}

export function placeFireflies(spec: FireflySpec, r: Region, baseSeed: number): PlacedFirefly[] {
  const out: PlacedFirefly[] = [];
  for (let i = 0; i < spec.count; i += 1) {
    const s = baseSeed + i * 7.31;
    const ang = seededRand(s + 4) * Math.PI * 2;
    out.push({
      x: lerp(r.x0, r.x1, seededRand(s)),
      y: lerp(r.y0, r.y1, seededRand(s + 1)),
      size: lerp(spec.sizeMin, spec.sizeMax, seededRand(s + 2)),
      color: spec.colors[Math.floor(seededRand(s + 3) * spec.colors.length) % spec.colors.length],
      periodMs: lerp(spec.periodMinMs, spec.periodMaxMs, seededRand(s + 5)),
      driftX: Math.cos(ang) * spec.driftPx,
      driftY: Math.sin(ang) * spec.driftPx,
      alphaMin: spec.alphaMin,
      alphaMax: spec.alphaMax,
      phase: seededRand(s + 6),
    });
  }
  return out;
}

export function placeGodRays(spec: GodRaySpec, r: Region, baseSeed: number): PlacedGodRay[] {
  const out: PlacedGodRay[] = [];
  for (let i = 0; i < spec.count; i += 1) {
    const s = baseSeed + 100 + i * 13.7;
    out.push({
      x: lerp(r.x0 + (r.x1 - r.x0) * 0.1, r.x1 - (r.x1 - r.x0) * 0.1, seededRand(s)),
      y: r.y0 + spec.height * 0.3,
      width: lerp(spec.widthMin, spec.widthMax, seededRand(s + 1)),
      height: spec.height,
      color: spec.color,
      tiltDeg: lerp(-spec.tiltDeg, spec.tiltDeg, seededRand(s + 2)),
      periodMs: lerp(spec.periodMinMs, spec.periodMaxMs, seededRand(s + 3)),
      alphaMin: spec.alphaMin,
      alphaMax: spec.alphaMax,
    });
  }
  return out;
}

export function placeGlowBlobs(blobs: readonly GlowBlobSpec[], r: Region): PlacedGlowBlob[] {
  return blobs.map((g) => {
    const position = resolveEffectAnchor(g, r);
    return {
      ...position,
      size: g.size,
      color: g.color,
      alpha: g.alpha,
    };
  });
}
